import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

const cardVariants = cva("relative overflow-hidden text-card-foreground transition-shadow", {
    variants: {
        variant: {
            default: "rounded-2xl border border-border bg-card shadow-sm",
            elevated: "rounded-2xl bg-card shadow-lg",
            flat: "rounded-2xl bg-muted",
            outline: "rounded-2xl border-2 border-border bg-transparent",
            accent: "rounded-2xl border border-border bg-card shadow-sm",
            journal:
                "rounded-2xl border-2 border-secondary bg-card shadow-[0_0_0_4px_color-mix(in_srgb,var(--color-secondary)_15%,transparent)]",
        },
        hoverable: {
            true: "cursor-pointer hover:shadow-md",
            false: "",
        },
        width: {
            fixed: "w-full max-w-[350px]",
            full: "w-full",
        },
    },
    defaultVariants: {
        variant: "default",
        hoverable: false,
        width: "fixed",
    },
});

export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, variant, hoverable, width, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant, hoverable, width, className }))} {...props} />
));
Card.displayName = "Card";

const CardImage = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("relative w-full overflow-hidden", className)} {...props}>
            {children}
        </div>
    ),
);
CardImage.displayName = "CardImage";

const CardAccentPanel = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { color?: "primary" | "secondary" }
>(({ className, color = "primary", ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            "flex w-24 shrink-0 items-center justify-center rounded-l-2xl",
            color === "primary" ? "bg-primary" : "bg-secondary",
            className,
        )}
        {...props}
    />
));
CardAccentPanel.displayName = "CardAccentPanel";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn("flex flex-col space-y-1.5 p-5", className)} {...props} />
    ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("font-display text-lg leading-snug font-bold tracking-tight", className)}
            {...props}
        />
    ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn("text-muted-foreground text-sm", className)} {...props} />
    ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => <div ref={ref} className={cn("px-5 pt-0 pb-5", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn("flex items-center px-5 pt-0 pb-5", className)} {...props} />
    ),
);
CardFooter.displayName = "CardFooter";

const CardIconSlot = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn("text-muted-foreground absolute top-4 right-4", className)} {...props} />
    ),
);
CardIconSlot.displayName = "CardIconSlot";

const CardMeta = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                "text-muted-foreground flex items-center justify-between font-mono text-xs font-medium tracking-wider uppercase",
                className,
            )}
            {...props}
        />
    ),
);
CardMeta.displayName = "CardMeta";

export {
    Card,
    CardImage,
    CardAccentPanel,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
    CardMeta,
    CardIconSlot,
    cardVariants,
};
